import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

export default function Home() {
  return (
    <>
      <Helmet>
        <title>RailTech – Secure AI Infrastructure for Autonomous Agents</title>
        <meta name="description" content="RailTech builds AgentPod, MME (the bounded tag-graph memory engine for AI agents, with an official Python SDK), and Guardian (MSE) – foundational tools for secure AI execution, agent memory, and developer monetization." />
        <meta name="keywords" content="AgentPod, RailTech, MME, agent memory, Python SDK, railtech-mme, LangChain memory, AI infrastructure, AI security, secure AI execution, agent monetization, MSE Guardian, model verification" />
        <meta name="author" content="RailTech Team" />
        <meta property="og:title" content="RailTech – Secure AI Infrastructure" />
        <meta property="og:description" content="Building secure infrastructure for the AI agent economy — AgentPod, MME (memory), and Guardian (security)." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://railtech.io" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative isolate overflow-hidden bg-white">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-10 sm:pb-24 lg:flex lg:px-8 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-8"
          >
            <h1 className="mt-10 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              Building Trust at the Edge of AI
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              RailTech is revolutionizing AI infrastructure with our secure, fast, and transparent solutions. 
              We're building the foundation for trustworthy AI applications.
            </p>
            <div className="mt-10 flex items-center gap-x-6">
              <Link
                to="/agentpod"
                className="rounded-md bg-[#6C63FF] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#5A52E0] transition-colors"
              >
                Explore Products
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      

      {/* Products Section */}
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mx-auto max-w-2xl text-center"
          >
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Our Products</h2>
            <p className="mt-2 text-lg leading-8 text-gray-600">
              Discover our innovative solutions designed to power the future of AI.
            </p>
          </motion.div>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col items-start"
            >
              <div className="rounded-2xl bg-gray-50 p-8 hover:shadow-lg transition-shadow h-full w-full">
                <h3 className="text-2xl font-bold leading-6 text-gray-900">AgentPod</h3>
                <p className="mt-4 text-base leading-7 text-gray-600">
                  A secure infrastructure platform for executing, verifying, and monetizing AI agents.
                </p>
                <Link to="/agentpod" className="mt-4 inline-block text-sm font-semibold leading-6 text-[#6C63FF] hover:text-[#5A52E0] transition-colors">
                  Learn more <span aria-hidden="true">→</span>
                </Link>
              </div>
            </motion.article>
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="flex flex-col items-start"
            >
              <div className="rounded-2xl bg-gray-50 p-8 hover:shadow-lg transition-shadow h-full w-full">
                <div className="flex items-center gap-2">
                  <h3 className="text-2xl font-bold leading-6 text-gray-900">MME</h3>
                  <span className="inline-flex items-center rounded-full bg-[#EEF2FF] px-2 py-0.5 text-xs font-medium text-[#4F46E5]">
                    New · Python SDK
                  </span>
                </div>
                <p className="mt-4 text-base leading-7 text-gray-600">
                  A bounded tag-graph memory engine for AI agents — token-budgeted packs,
                  online learning, and an official Python SDK with LangChain integration
                  (<code className="text-sm">pip install railtech-mme</code>).
                </p>
                <a href="https://mme.railtech.io" target="_blank" rel="noreferrer" className="mt-4 inline-block text-sm font-semibold leading-6 text-[#6C63FF] hover:text-[#5A52E0] transition-colors">
                  Try MME <span aria-hidden="true">→</span>
                </a>
              </div>
            </motion.article>
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-col items-start"
            >
              <div className="rounded-2xl bg-gray-50 p-8 hover:shadow-lg transition-shadow h-full w-full">
                <h3 className="text-2xl font-bold leading-6 text-gray-900">Guardian MSE</h3>
                <p className="mt-4 text-base leading-7 text-gray-600">
                  Our upcoming Modular Security Engine for AI platforms.
                </p>
                <Link to="/mse" className="mt-4 inline-block text-sm font-semibold leading-6 text-[#6C63FF] hover:text-[#5A52E0] transition-colors">
                  Learn more <span aria-hidden="true">→</span>
                </Link>
              </div>
            </motion.article>
          </div>
        </div>
      </div>

      {/* Value Proposition Section */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mx-auto max-w-2xl lg:text-center"
          >
            <h2 className="text-base font-semibold leading-7 text-[#6C63FF]">Why Choose RailTech</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Everything you need to build trustworthy AI
            </p>
          </motion.div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {[
                {
                  title: 'Security',
                  description: 'Built-in security features to protect your AI models and data.'
                },
                {
                  title: 'Speed',
                  description: 'Optimized performance for edge computing and real-time applications.'
                },
                {
                  title: 'Transparency',
                  description: 'Clear visibility into AI model behavior and data lineage.'
                }
              ].map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex flex-col"
                >
                  <dt className="text-base font-semibold leading-7 text-gray-900">{feature.title}</dt>
                  <dd className="mt-1 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">{feature.description}</p>
                  </dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>
      </div>
      {/* Backed By Section */}
<div className="bg-white border-t border-gray-100">
  <div className="mx-auto max-w-7xl px-6 py-10 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="text-center"
    >
      <h2 className="text-sm font-medium tracking-wide text-gray-700">
        Backed by Leading Incubators & Programs
      </h2>
      <p className="mt-2 text-sm text-gray-500">
        We’re proud to be part of these innovation ecosystems
      </p>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="mt-8 flex flex-wrap items-center justify-center gap-x-16 gap-y-8"
      aria-label="Programs and communities"
    >
      {[
  { name: 'DMZ', src: '/DMZ-logo.png', alt: 'DMZ logo' },
  { name: 'YSpace', src: '/yspace-logo', alt: 'YSpace logo' },
  { name: 'Italia360', src: '/italia360-logo.avif', alt: 'Italia360 logo' },
].map((logo) => (
  <div key={logo.name} className="flex items-center justify-center">
    <img
      src={logo.src}
      alt={logo.alt}
      className={`w-auto opacity-80 grayscale hover:grayscale-0 hover:opacity-100 transition 
        ${logo.name === 'YSpace' ? 'h-14 sm:h-16' : 'h-10 sm:h-12'}`}
      loading="lazy"
      decoding="async"
    />
  </div>
))}

    </motion.div>

    
  </div>
</div>
    </>
  );
}
